import { Component, DestroyRef, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DaisyDrawerComponent, DaisyTextboxComponent } from '@pathfinder/daisy-controls/ui';
import { FormUtility } from '@lib/shared/util';
import { AuthService } from '@lib/auth/data-access';
import { TenantService } from '@lib/tenant/data-access';
import { FormsModule } from '@angular/forms';
import { BroadcastService, EnvironmentService } from '@lib/shared/data-access';
import { AuthAccessibleTenantModel } from '@lib/auth/models';
import { Router, RouterModule } from '@angular/router';


@Component({
  selector: 'lib-tenant-selection-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    DaisyDrawerComponent,
    DaisyTextboxComponent,
    FormsModule,
    RouterModule
  ],
  templateUrl: './tenant-selection-sidebar.component.html',
  styleUrl: './tenant-selection-sidebar.component.scss',
})
export class TenantSelectionSidebarComponent extends FormUtility {
  

  public authService = inject(AuthService);
  private router = inject(Router);
  public tenantService = inject(TenantService);
  private destroyRef = inject(DestroyRef);
  private environmentService = inject(EnvironmentService);
  private broadcastService = inject(BroadcastService);
  private isGetTenantsCompleted = false;
  
  public tenants: AuthAccessibleTenantModel[] = [];
  public tenantsFiltered: AuthAccessibleTenantModel[] = [];

  /** The search box's text. Held here because `daisy-textbox` binds a value rather than reading the DOM. */
  public tenantFilter = '';
  public isSwitchingTenant = false;
  private counter = 1;
  public canAccessInactiveTenants = this.authService.userPrincipalComputedSignal()?.isCodyAdministrator;

  private tenantEffect = effect(() => {
    if (this.authService.userPrincipalComputedSignal()) {
      const tenantInUse = this.authService.userPrincipalComputedSignal()!.accessibleTenants.find(t => t.externalId === this.authService.currentTenantExternalIdComputedSignal());
      const tempTenants = [...this.authService.userPrincipalComputedSignal()!.accessibleTenants].filter(t => t.externalId !== this.authService.currentTenantExternalIdComputedSignal());

      tempTenants.unshift(tenantInUse!); //Put current tenant in use at the top of the list

      this.tenants = [...tempTenants];
      this.tenantsFiltered = [...tempTenants];
      this.counter++;
    }
  });
  
  /** `daisy-textbox` emits the value itself, where the native input handed over an event to dig it out of. */
  onTenantFilter(value: string): void {
    this.tenantFilter = value;

    if (value.length > 0) {
      this.tenantsFiltered = this.tenants.filter(tenant => tenant.tenantName.toLowerCase().includes(value.toLowerCase()) 
        || tenant.tenantOri.toLowerCase().includes(value.toLowerCase()));
    }
    else {
      this.tenantsFiltered = [...this.tenants];
    }
  }

  onTenantSelect(event: MouseEvent, tenant: AuthAccessibleTenantModel): void {
    const currentTenantId = this.authService.userPrincipalComputedSignal()?.currentTenantExternalId;

    if(tenant.isActive === false && !this.canAccessInactiveTenants) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }

    // Prevent action if it's the current tenant or already processing
    if (currentTenantId === tenant.externalId || this.isSwitchingTenant) {
      event.preventDefault(); // Prevent even default link behavior if disabled
      return;
    }

    // --- Check for middle-click, Ctrl+click, Cmd+click ---
    // If it's any of these, let the browser handle opening in a new tab/window via the href
    if (event.ctrlKey || event.metaKey || event.button === 1) {
      // We don't preventDefault here, let the browser do its thing with the href
      // Optionally close the drawer
      this.tenantService.isTenantSelectionSidebarVisibleSignal.set(false);
      return;
    }

    // --- Handle standard left-click (Force Refresh) ---
    event.preventDefault(); // IMPORTANT: Stop Angular routerLink and default <a> navigation

    // Set switching state
    this.isSwitchingTenant = true;
    this.tenantService.isTenantSelectionSidebarVisibleSignal.set(false);

    if (this.environmentService.isElectron) {
       // In Electron the app runs under a custom app:// protocol with HashLocationStrategy.
      // window.location.href = '/path' causes a chrome-error because it navigates to a
      // non-existent file. Instead, set the hash directly (which is synchronous and
      // guaranteed to be in window.location before reload() fires), then reload.
      // On web, window.location.href works fine with the normal path-based strategy.
      const targetPath = `/t/${tenant.externalId}/dashboard`;
      window.location.hash = targetPath;
      window.location.reload();
    }
    else {
      // Construct the target URL (same logic as routerLink)
      const targetUrl = this.router.createUrlTree(['/t', tenant.externalId, 'dashboard']).toString();

      // Force a full page reload to the new tenant's URL
      window.location.href = targetUrl;
    }
    }

    onDrawerHide(): void {
      // TODO fix with primeng animation transitions.
        // Create bug in GitHub for primeng to fix it. The animations take too long and the overlay is still there, users can't click anything for a bit.
        const maskElement = document.querySelector(
            '.p-drawer-mask'
        );
        if (maskElement) {
            maskElement.classList.remove('p-overlay-mask')
        }
    }
  }
